import { useMemo, useState } from 'react';
import { ChevronDown, FileText, Settings2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NoteTemplateManager } from '@/components/NoteTemplateManager';
import { useNoteTemplates } from '@/api/hooks';
import { applyTemplate, TEMPLATE_VARIABLES, type TemplateContext } from '@/lib/note-templates';
import { cn } from '@/lib/utils';
import type { NoteTemplate } from '@/api/client';

interface Props {
  /** Ticket data the template's {{placeholders}} are filled from. */
  context: TemplateContext;
  /** Receives the expanded text — the caller drops it into the note box for editing. */
  onInsert: (text: string, template: NoteTemplate) => void;
  className?: string;
}

const LABELS = new Map(TEMPLATE_VARIABLES.map((v) => [v.token, v.label]));

/**
 * Dropdown that inserts a saved note template into the note box. It never posts the
 * note — the engineer always gets to read and edit the filled-in text first.
 */
export function NoteTemplatePicker({ context, onInsert, className }: Props) {
  const templates = useNoteTemplates();
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [query, setQuery] = useState('');

  const items = templates.data?.items ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (t) => t.title.toLowerCase().includes(q) || t.body.toLowerCase().includes(q),
    );
  }, [items, query]);

  const mine = filtered.filter((t) => t.isOwner);
  const shared = filtered.filter((t) => !t.isOwner);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const insert = (t: NoteTemplate) => {
    const { text, unresolved } = applyTemplate(t.body, context);
    onInsert(text, t);
    setOpen(false);
    if (unresolved.length > 0) {
      // Left in the text as {{token}} rather than blanked, so it can't be sent unnoticed.
      toast.warning('Some details could not be filled in', {
        description: `${unresolved.map((tok) => LABELS.get(tok) ?? tok).join(', ')} — edit before sending.`,
      });
    }
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground',
            'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            className,
          )}
        >
          <FileText className="h-3.5 w-3.5"/>
          Templates
          <ChevronDown className="h-3 w-3 opacity-50"/>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-80 overflow-hidden p-0">
          {items.length > 4 && (
            <>
              <div className="p-2">
                <Input
                  autoFocus
                  placeholder="Search templates…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-8"
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <DropdownMenuSeparator className="my-0"/>
            </>
          )}

          <div className="max-h-80 overflow-y-auto overflow-x-hidden py-1">
            {templates.isLoading && <DropdownMenuItem disabled>Loading templates…</DropdownMenuItem>}

            {!templates.isLoading && items.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                No templates yet.
              </div>
            )}

            {!templates.isLoading && items.length > 0 && filtered.length === 0 && (
              <DropdownMenuItem disabled>No matches</DropdownMenuItem>
            )}

            {mine.length > 0 && <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wide text-muted-foreground">Mine</DropdownMenuLabel>}
            {mine.map((t) => <TemplateItem key={t.id} template={t} onSelect={() => insert(t)}/>)}

            {shared.length > 0 && (
              <>
                {mine.length > 0 && <DropdownMenuSeparator/>}
                <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wide text-muted-foreground">Shared</DropdownMenuLabel>
                {shared.map((t) => <TemplateItem key={t.id} template={t} onSelect={() => insert(t)}/>)}
              </>
            )}
          </div>

          <DropdownMenuSeparator className="my-0"/>
          <DropdownMenuItem
            className="gap-2 text-xs text-muted-foreground"
            onSelect={() => setManageOpen(true)}
          >
            <Settings2 className="h-3.5 w-3.5"/> Manage templates…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Note templates</DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto px-6 pb-6">
            <NoteTemplateManager/>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TemplateItem({ template, onSelect }: { template: NoteTemplate; onSelect: () => void }) {
  return (
    <DropdownMenuItem
      onSelect={(e) => { e.preventDefault(); onSelect(); }}
      className="flex flex-col items-start gap-0.5"
    >
      <span className="flex w-full items-center gap-1.5">
        <span className="truncate text-[12.5px]">{template.title}</span>
        {!template.isOwner && <Share2 className="h-2.5 w-2.5 shrink-0 opacity-60"/>}
        {template.isInternal && (
          <span className="ml-auto shrink-0 rounded bg-amber-500/15 px-1 text-[10px] text-amber-600 dark:text-amber-400">
            Internal
          </span>
        )}
      </span>
      <span className="line-clamp-2 w-full whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
        {template.body}
      </span>
    </DropdownMenuItem>
  );
}
